import React from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import MenuItem from '@material-ui/core/MenuItem'
import MenuList from '@material-ui/core/MenuList'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import Paper from '@material-ui/core/Paper'
import Grow from '@material-ui/core/Grow'
import Divider from '@material-ui/core/Divider'
import Icon from '@material-ui/core/Icon'
import Popper from '@material-ui/core/Popper'
import Button from 'components/CustomButtons/Button.js'

const cyberpunkDropdownStyles = makeStyles({
  dropdown: {
    borderRadius: '3px',
    boxShadow: '0 0 20px rgba(255, 0, 255, 0.3)',
    top: '100%',
    zIndex: '1000',
    minWidth: '160px',
    padding: '5px 0',
    margin: '2px 0 0',
    fontSize: '14px',
    textAlign: 'left',
    listStyle: 'none',
    backgroundColor: 'rgba(10, 10, 10, 0.98)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 0, 255, 0.3)',
    backgroundClip: 'padding-box'
  },
  menuList: {
    padding: '0'
  },
  popperClose: {
    pointerEvents: 'none'
  },
  popperResponsive: {
    zIndex: '1200',
    '&[x-placement*=bottom] $dropdown': {
      marginTop: '0px',
      marginBottom: '0px'
    },
    '&[x-placement*=top] $dropdown': {
      marginTop: '0px',
      marginBottom: '0px'
    },
    '&[x-placement*=right] $dropdown': {
      marginLeft: '0px',
      marginRight: '0px'
    },
    '&[x-placement*=left] $dropdown': {
      marginLeft: '0px',
      marginRight: '0px'
    }
  },
  dropdownItem: {
    fontSize: '13px',
    padding: '10px 20px',
    margin: '0 5px',
    borderRadius: '2px',
    position: 'relative',
    transition: 'all 150ms linear',
    display: 'block',
    clear: 'both',
    fontWeight: '400',
    color: '#00ffff !important',
    fontFamily: '"Share Tech Mono", monospace',
    textShadow: '0 0 3px rgba(0, 255, 255, 0.3)',
    whiteSpace: 'nowrap',
    minHeight: 'unset',
    '&:hover': {
      backgroundColor: 'rgba(255, 0, 255, 0.1)',
      color: '#ff00ff !important',
      textShadow: '0 0 8px rgba(255, 0, 255, 0.6)',
      boxShadow: 'inset 0 0 10px rgba(255, 0, 255, 0.2)',
    }
  },
  primaryHover: {
    '&:hover': {
      backgroundColor: 'rgba(255, 0, 255, 0.1)',
      color: '#ff00ff !important'
    }
  },
  dropdownItemRTL: {
    textAlign: 'right'
  },
  dropdownDividerItem: {
    margin: '5px 0',
    backgroundColor: 'rgba(255, 0, 255, 0.3)',
    height: '1px',
    overflow: 'hidden'
  },
  buttonIcon: {
    width: '20px',
    height: '20px',
    marginRight: '5px',
    filter: 'drop-shadow(0 0 3px rgba(0, 255, 255, 0.5))',
  },
  caret: {
    transition: 'all 150ms ease-in',
    display: 'inline-block',
    width: '0',
    height: '0',
    marginLeft: '4px',
    verticalAlign: 'middle',
    borderTop: '4px solid',
    borderRight: '4px solid transparent',
    borderLeft: '4px solid transparent',
    borderTopColor: '#00ffff',
    filter: 'drop-shadow(0 0 2px rgba(0, 255, 255, 0.5))',
  },
  caretActive: {
    transform: 'rotate(180deg)',
    borderTopColor: '#ff00ff',
    filter: 'drop-shadow(0 0 3px rgba(255, 0, 255, 0.7))',
  },
  caretRTL: {
    marginRight: '4px',
    marginLeft: '0'
  },
  dropdownHeader: {
    display: 'block',
    padding: '0.1875rem 1.25rem',
    fontSize: '0.75rem',
    lineHeight: '1.428571',
    color: '#ff00ff',
    fontFamily: '"Orbitron", monospace',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    textShadow: '0 0 5px rgba(255, 0, 255, 0.5)',
    whiteSpace: 'nowrap'
  },
  noLiPadding: {
    padding: '0'
  }
})

export default function CyberpunkDropdown ({
  buttonText,
  buttonIcon,
  dropdownList,
  buttonProps,
  dropup,
  dropdownHeader,
  caret = true,
  hoverColor = 'primary',
  left,
  rtlActive,
  noLiPadding,
  onClick
}) {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const handleClick = event => {
    if (anchorEl && anchorEl.contains(event.target)) {
      setAnchorEl(null)
    } else {
      setAnchorEl(event.currentTarget)
    }
  }
  const handleClose = param => {
    setAnchorEl(null)
    if (onClick) {
      onClick(param)
    }
  }
  const handleCloseAway = event => {
    if (anchorEl && anchorEl.contains(event.target)) {
      return
    }
    setAnchorEl(null)
  }
  const classes = cyberpunkDropdownStyles()
  const caretClasses = classNames({
    [classes.caret]: true,
    [classes.caretActive]: Boolean(anchorEl),
    [classes.caretRTL]: rtlActive
  })
  const dropdownItem = classNames({
    [classes.dropdownItem]: true,
    [classes[hoverColor + 'Hover']]: true,
    [classes.noLiPadding]: noLiPadding,
    [classes.dropdownItemRTL]: rtlActive
  })
  let icon = null
  switch (typeof buttonIcon) {
    case 'object':
      const ButtonIconComponent = buttonIcon
      icon = <ButtonIconComponent className={classes.buttonIcon}/>
      break
    case 'string':
      icon = <Icon className={classes.buttonIcon}>{buttonIcon}</Icon>
      break
    default:
      icon = null
      break
  }
  return (
    <div>
      <div>
        <Button
          aria-label="Notifications"
          aria-owns={anchorEl ? 'menu-list' : null}
          aria-haspopup="true"
          {...buttonProps}
          onClick={handleClick}
        >
          {icon}
          {buttonText !== undefined ? buttonText : null}
          {caret ? <b className={caretClasses}/> : null}
        </Button>
      </div>
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        transition
        disablePortal
        placement={
          dropup
            ? left
              ? 'top-start'
              : 'top'
            : left
              ? 'bottom-start'
              : 'bottom'
        }
        className={classNames({
          [classes.popperClose]: !anchorEl,
          [classes.popperResponsive]: true
        })}
      >
        {() => (
          <Grow
            in={Boolean(anchorEl)}
            id="menu-list"
            style={
              dropup
                ? { transformOrigin: '0 100% 0' }
                : { transformOrigin: '0 0 0' }
            }
          >
            <Paper className={classes.dropdown}>
              <ClickAwayListener onClickAway={handleCloseAway}>
                <MenuList role="menu" className={classes.menuList}>
                  {dropdownHeader !== undefined ? (
                    <MenuItem
                      onClick={() => handleClose(dropdownHeader)}
                      className={classes.dropdownHeader}
                    >
                      {dropdownHeader}
                    </MenuItem>
                  ) : null}
                  {dropdownList.map((prop, key) => {
                    if (prop.divider) {
                      return (
                        <Divider
                          key={key}
                          onClick={() => handleClose('divider')}
                          className={classes.dropdownDividerItem}
                        />
                      )
                    }
                    return (
                      <MenuItem
                        key={key}
                        onClick={() => handleClose(prop)}
                        className={dropdownItem}
                      >
                        {prop}
                      </MenuItem>
                    )
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  )
}

CyberpunkDropdown.propTypes = {
  hoverColor: PropTypes.oneOf([
    'black',
    'primary',
    'info',
    'success',
    'warning',
    'danger',
    'rose'
  ]),
  buttonText: PropTypes.node,
  buttonIcon: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  dropdownList: PropTypes.array,
  buttonProps: PropTypes.object,
  dropup: PropTypes.bool,
  dropdownHeader: PropTypes.node,
  rtlActive: PropTypes.bool,
  caret: PropTypes.bool,
  left: PropTypes.bool,
  noLiPadding: PropTypes.bool,
  onClick: PropTypes.func
}
